import React from 'react';
import { ArrowRight, Shield, Zap, CheckCircle, Sparkles, Brain, Target, TrendingUp } from 'lucide-react';
import AnimatedTitle from './AnimatedTitle';

interface HeroSectionProps {
  isLoggedIn: boolean;
  onStartTraining: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  isLoggedIn,
  onStartTraining,
}) => {
  // 移除原有的trustIndicators数组，直接在JSX中定义

  return (
    <section id="hero-section" className="relative min-h-[75vh] sm:min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 pt-16 sm:pt-20 pb-6 sm:pb-8 overflow-hidden">
      {/* 增强的背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        {/* 主要背景blob */}
        <div className="absolute -top-40 -right-32 w-80 h-80 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-32 w-80 h-80 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        
        {/* 额外的装饰元素 */}
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-r from-cyan-300 to-blue-400 rounded-full mix-blend-multiply filter blur-2xl opacity-15 animate-float"></div>
        <div className="absolute bottom-1/3 right-1/4 w-24 h-24 bg-gradient-to-r from-purple-300 to-pink-400 rounded-full mix-blend-multiply filter blur-2xl opacity-15 animate-float animation-delay-4000"></div>
        
        {/* 几何装饰 */}
        <div className="absolute top-1/2 left-10 w-2 h-2 bg-blue-400 rounded-full opacity-30 animate-bounce-subtle"></div>
        <div className="absolute top-1/3 right-20 w-3 h-3 bg-indigo-400 rounded-full opacity-25 animate-bounce-subtle animation-delay-200"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-purple-400 rounded-full opacity-40 animate-bounce-subtle animation-delay-400"></div>
        
        {/* 渐变网格背景 */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/30 to-transparent animate-gradient opacity-50"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* AI标签 - 突出每日精选和AI分析特色 */}
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 border border-blue-200 text-blue-700 rounded-full text-sm font-medium mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 mr-2 animate-spin" style={{ animationDuration: '3s' }} />
            <span className="text-blue-800 font-semibold">每日精选 • AI深度分析</span>
          </div>

          {/* 主标题 - 移动端优化 */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight px-4">
            <span className="block text-gray-900 mb-3 animate-slide-up animation-delay-100">
              从新闻热点中
            </span>
            <div className="block animate-slide-up animation-delay-200 flex items-center justify-center flex-wrap">
              <AnimatedTitle
                text="学会深度思考"
                animationType="gradientShift"
                duration={2000}
                delay={500}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
              />
              <Brain className="inline-block w-6 h-6 sm:w-8 sm:h-8 ml-2 sm:ml-4 text-blue-500 animate-bounce-subtle" />
            </div>
          </h1>

          {/* 副标题 - 移动端优化 */}
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed animate-fade-in animation-delay-300 px-4">
            基于热点新闻的深度案例分析，AI专家级解读，让思维过程变得清晰可见
          </p>

          {/* CTA按钮组 - 移动端优化的主要按钮 */}
          <div className="flex justify-center items-center mb-12 animate-slide-up animation-delay-400 px-4">
            <button
              onClick={onStartTraining}
              className="button-enhanced mobile-button-enhanced bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-xl text-lg sm:text-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-2xl flex items-center group relative w-full sm:w-auto max-w-sm"
            >
              <Target className="w-6 h-6 mr-3 group-hover:rotate-12 transition-transform duration-200" />
              {isLoggedIn ? '继续学习之旅' : '开始案例学习'}
              <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>

          {/* 信任指标 - 移动端优化 */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 animate-fade-in animation-delay-500 px-4">
            {[
              { icon: CheckCircle, text: '完全免费', color: 'text-emerald-500' },
              { icon: Zap, text: '每日更新', color: 'text-amber-500' },
              { icon: Shield, text: 'AI专家分析', color: 'text-blue-500' },
            ].map((indicator, index) => {
              const Icon = indicator.icon;
              return (
                <div key={index} className="trust-indicator flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-lg bg-white/50 backdrop-blur-sm border border-white/20 shadow-sm touch-target">
                  <Icon className={`trust-icon w-4 h-4 sm:w-5 sm:h-5 ${indicator.color}`} style={{ animationDelay: `${index * 0.2}s` }} />
                  <span className="text-gray-700 font-medium text-sm">{indicator.text}</span>
                </div>
              );
            })}
          </div>
          
          {/* 案例预览提示 - 移动端优化 */}
          <div className="mt-6 sm:mt-8 flex flex-col items-center animate-fade-in animation-delay-600 px-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              <p className="text-sm text-gray-600 font-medium">今日热门案例分析</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs text-gray-500">
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium whitespace-nowrap">🔥 国际局势</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium whitespace-nowrap">💼 商业分析</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium whitespace-nowrap">🧠 思维模式</span>
            </div>
            <div className="mt-4 w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-bounce"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;